import { z } from 'zod/v4';
import { AdminAllowedOriginSchema } from './admin-create-allowed-origin.js';

export const AdminAllowedOriginsListSortColumns = [
  'updatedAt',
  'companyName',
  'name',
  'prettyOrigin',
  'origin',
] as const;

export type AdminAllowedOriginsListSortColumn =
  (typeof AdminAllowedOriginsListSortColumns)[number];

const sortColumns = AdminAllowedOriginsListSortColumns.join('|');

// One or more comma-separated `<column>:<ASC|DESC>` terms, e.g.
// `companyName:ASC,updatedAt:DESC` — the same grammar the legacy route accepted.
const sortPattern = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

// Legacy also took `q`, but never put it in the query; it is left out, and an
// unknown param is ignored.
export const AdminAllowedOriginsListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(sortPattern).optional(),
  // JSON object keyed by `companyName`, `name`, `prettyOrigin` or `origin`,
  // each a case-insensitive substring match. Malformed JSON, unknown keys and
  // empty values are ignored, not errors.
  filter: z.string().max(512).optional(),
  // Every matching row in one response, unpaged — what the webshops screens use
  // to resolve the allowed-origin ids a webshop carries. `page`/`count` are
  // ignored; `filter` and `sort` still apply.
  isDropDown: booleanLike,
});

export type AdminAllowedOriginsListQuery = z.infer<
  typeof AdminAllowedOriginsListQuerySchema
>;

// The platform's paginated envelope. `pagination.page` is 1-based, unlike the
// 0-based `page` query param. Legacy sent `{ data, meta: { count, page,
// pageSize, totalCount } }`. In `isDropDown` mode the one page holds every row.
export const AdminAllowedOriginsListResponseSchema = z.object({
  metadata: z.object({
    success: z.boolean(),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
  data: z.array(AdminAllowedOriginSchema),
});

export type AdminAllowedOriginsListResponse = z.infer<
  typeof AdminAllowedOriginsListResponseSchema
>;
